import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import BatchResults from './Components/BatchResults';
import ResolutionContainer from './Components/ResolutionContainer';

const BATCH_CHUNK_SIZE = 250;

const sizeOptions = [
  { value: 'hires', label: 'High Resolution' },
  { value: 'large', label: 'Large' },
  { value: 'small', label: 'Small' },
  { value: 'thumbnail', label: 'Thumbnail' },
];

const hasImagesInVariants = (variants) =>
  Array.isArray(variants) &&
  variants.some((variant) => Array.isArray(variant) && variant.length > 0);

const hasImagesInObject = (obj) =>
  obj && typeof obj === 'object' && hasImagesInVariants(Object.values(obj));

const collectDownloadKeys = (entries = []) =>
  Array.from(
    new Set(
      entries
        .map((entry) =>
          entry && entry.picRef
            ? String(entry.picRef).trim()
            : entry && entry.itemNumber
            ? String(entry.itemNumber).trim()
            : null
        )
        .filter(Boolean)
    )
  );

const filterItemNumbers = (values) => [
  ...new Set(values.map((value) => value.trim()).filter((value) => /^[0-9A-Za-z_-]+$/.test(value))),
];

const parseCsvFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      if (typeof text !== 'string') {
        reject(new Error('Unable to read file contents.'));
        return;
      }

      const values = text
        .split(/\r?\n/)
        .flatMap((line) => line.split(','))
        .map((entry) => entry.trim().replace(/^"|"$/g, ''))
        .filter(Boolean);

      resolve(values);
    };
    reader.onerror = () => reject(reader.error || new Error('Unable to read file.'));
    reader.readAsText(file);
  });

const formatDuration = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  }
  return `${seconds}s`;
};

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 100 ? Math.round(value) : value.toFixed(value >= 10 ? 1 : 2)} ${units[unitIndex]}`;
};

const ImageLibrary = () => {
  const userPermission = useSelector((state) => state.userPermission.value);
  const canDelete = Boolean(userPermission?.[1017]);
  const [searchParams] = useSearchParams();
  const itemsQueryRef = useRef('');

  const [imageInfo, setImageInfo] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [batchResults, setBatchResults] = useState(null);
  const [batchPage, setBatchPage] = useState(1);
  const [downloadPicRefs, setDownloadPicRefs] = useState([]);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [includeAlternatesOption, setIncludeAlternatesOption] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isGeneratingShareLink, setIsGeneratingShareLink] = useState(false);
  const [sharedDownloadLink, setSharedDownloadLink] = useState('');
  const [downloadProgressPercent, setDownloadProgressPercent] = useState(0);
  const [downloadStatusStartedAt, setDownloadStatusStartedAt] = useState(null);
  const [downloadStatusElapsedSeconds, setDownloadStatusElapsedSeconds] = useState(0);
  const [activeDownloadSummary, setActiveDownloadSummary] = useState({ fileCount: 0, itemCount: 0 });
  const [shareLinkJobId, setShareLinkJobId] = useState('');
  const [shareLinkJob, setShareLinkJob] = useState(null);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const [displayItemHeader, setDisplayItemHeader] = useState('');
  const [searchError, setSearchError] = useState(null);
  const [selectedImageKeys, setSelectedImageKeys] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [filters] = useState({
    sizes: [],
    formats: [],
    angles: [],
    missingHires: false,
    missingThumbnail: false,
  });

  const resetSingleView = () => {
    setBatchResults(null);
    setBatchPage(1);
    setImageInfo([]);
    setDownloadPicRefs([]);
    setDownloadModalOpen(false);
    setSelectedSizes([]);
    setIncludeAlternatesOption(false);
    setSharedDownloadLink('');
    setIsGeneratingShareLink(false);
    setShareLinkJobId('');
    setShareLinkJob(null);
    setLoadingProgress({ current: 0, total: 0 });
    setDownloadProgressPercent(0);
    setDisplayItemHeader('');
    setSearchError(null);
    setSelectedImageKeys([]);
    setConfirmDeleteOpen(false);
  };

  const fetchResultsInChunks = async (values, endpoint, payloadKey) => {
    const total = values.length;
    if (total === 0) {
      setLoadingProgress({ current: 0, total: 0 });
      return [];
    }

    const totalChunks = Math.ceil(total / BATCH_CHUNK_SIZE);
    let aggregatedResults = [];
    setLoadingProgress({ current: 0, total: totalChunks });

    for (let start = 0; start < total; start += BATCH_CHUNK_SIZE) {
      const chunk = values.slice(start, start + BATCH_CHUNK_SIZE);
      const response = await axios.post(endpoint, { [payloadKey]: chunk });
      aggregatedResults = aggregatedResults.concat(response.data.results || []);
      setLoadingProgress({
        current: Math.min(totalChunks, Math.floor(start / BATCH_CHUNK_SIZE) + 1),
        total: totalChunks,
      });
    }

    return aggregatedResults;
  };

  const fetchPicRefForItem = async (itemNumber) => {
    const response = await axios.get(`/imageLibrary/picRef/${itemNumber}`);
    if (!Array.isArray(response.data) || response.data.length === 0) {
      throw new Error(`Picture Reference Does Not Exist For Item #${itemNumber}`);
    }
    return response.data[0].picRef;
  };

  const fetchImagesForPicRef = async (picRef) => {
    const response = await axios.get(`/imageLibrary/aws/${picRef}`);
    const picRefData = response.data[picRef];
    if (!picRefData) {
      throw new Error(`No image data found for Picture Reference ${picRef}.`);
    }
    return picRefData;
  };

  useEffect(() => {
    const itemsParam = searchParams.get('items');
    if (!itemsParam) {
      itemsQueryRef.current = '';
      return;
    }
    if (itemsParam === itemsQueryRef.current) {
      return;
    }
    itemsQueryRef.current = itemsParam;

    const filteredItems = filterItemNumbers(
      itemsParam
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    );

    if (!filteredItems.length) {
      return;
    }

    const loadFromQuery = async () => {
      setItemSearch(filteredItems[0] || '');
      setDisplayItemHeader(filteredItems[0] || '');
      resetSingleView();
      setIsLoadingResults(true);
      setLoadingProgress({ current: 0, total: 0 });

      try {
        const results = await fetchResultsInChunks(
          filteredItems,
          '/imagelibrary/batch/items',
          'itemNumbers'
        );
        setBatchResults(results);
        setBatchPage(1);
        setDownloadPicRefs(collectDownloadKeys(results));
      } catch (error) {
        console.error('Error loading image library items from query:', error);
        alert('Unable to load the requested images.');
      } finally {
        setIsLoadingResults(false);
        setLoadingProgress({ current: 0, total: 0 });
      }
    };

    loadFromQuery();
  }, [searchParams]);

  useEffect(() => {
    if (!isDownloading && !isGeneratingShareLink) {
      setDownloadStatusElapsedSeconds(0);
      return undefined;
    }

    const startTime = downloadStatusStartedAt || Date.now();
    setDownloadStatusElapsedSeconds(Math.max(0, Math.floor((Date.now() - startTime) / 1000)));

    const intervalId = window.setInterval(() => {
      setDownloadStatusElapsedSeconds(Math.max(0, Math.floor((Date.now() - startTime) / 1000)));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [downloadStatusStartedAt, isDownloading, isGeneratingShareLink]);

  useEffect(() => {
    if (!isGeneratingShareLink || !shareLinkJobId) {
      return undefined;
    }

    let cancelled = false;

    const pollJobStatus = async () => {
      try {
        const response = await axios.get(`/imagelibrary/download/share-link/jobs/${shareLinkJobId}`);
        if (cancelled) {
          return;
        }

        const nextJob = response?.data || null;
        setShareLinkJob(nextJob);
        setActiveDownloadSummary({
          fileCount: nextJob?.fileCount || 0,
          itemCount: nextJob?.itemCount || 0,
        });

        if (nextJob?.status === 'completed' && nextJob?.url) {
          setSharedDownloadLink(nextJob.url);
          setIsGeneratingShareLink(false);
          setDownloadStatusStartedAt(null);
          setShareLinkJobId('');
          return;
        }

        if (nextJob?.status === 'failed') {
          setIsGeneratingShareLink(false);
          setDownloadStatusStartedAt(null);
          setShareLinkJobId('');
          alert(nextJob?.error || 'Unable to generate a shareable download link with the selected options.');
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('Error polling share link job:', error);
        setIsGeneratingShareLink(false);
        setDownloadStatusStartedAt(null);
        setShareLinkJobId('');
        alert('Unable to retrieve the share link job status.');
      }
    };

    pollJobStatus();
    const intervalId = window.setInterval(pollJobStatus, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isGeneratingShareLink, shareLinkJobId]);

  const handleItemInput = (event) => {
    const digitsOnly = event.target.value.replace(/\D+/g, '');
    setItemSearch(digitsOnly);
  };

  const handleItemSearch = async () => {
    if (!itemSearch) {
      return;
    }

    resetSingleView();
    setIsLoadingResults(true);
    setLoadingProgress({ current: 0, total: 0 });
    setSearchError(null);

    try {
      const picRef = await fetchPicRefForItem(itemSearch);
      const picRefData = await fetchImagesForPicRef(picRef);
      const { small = [], large = [], hires = [], thumbnail = [] } = picRefData;

      setImageInfo([hires, large, small, thumbnail]);
      setDisplayItemHeader(itemSearch);
      setDownloadPicRefs(
        hasImagesInVariants([hires, large, small, thumbnail]) ? [String(picRef).trim()] : []
      );
    } catch (error) {
      console.error('Error retrieving item images:', error);
      setSearchError('Item Number Not Found');
    } finally {
      setIsLoadingResults(false);
      setLoadingProgress({ current: 0, total: 0 });
    }
  };

  const handleItemEnterSubmit = async (event) => {
    if (event.key === 'Enter') {
      await handleItemSearch();
    }
  };

  const handleItemFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      resetSingleView();
      setIsLoadingResults(true);
      setLoadingProgress({ current: 0, total: 0 });

      const values = await parseCsvFile(file);
      const itemNumbers = filterItemNumbers(values);

      if (itemNumbers.length === 0) {
        alert('No valid item numbers found in the uploaded file.');
        return;
      }

      const results = await fetchResultsInChunks(itemNumbers, '/imagelibrary/batch/items', 'itemNumbers');
      setBatchResults(results);
      setImageInfo([]);
      setBatchPage(1);

      const downloadable = Array.from(
        new Set(
          results
            .filter((entry) => entry.images && hasImagesInObject(entry.images))
            .map((entry) =>
              entry.picRef
                ? String(entry.picRef).trim()
                : entry.itemNumber
                ? String(entry.itemNumber).trim()
                : null
            )
            .filter(Boolean)
        )
      );

      setDownloadPicRefs(downloadable.length ? downloadable : collectDownloadKeys(results));
    } catch (error) {
      console.error('Error processing item CSV upload:', error);
      alert('Unable to process the uploaded item CSV file.');
    } finally {
      event.target.value = '';
      setIsLoadingResults(false);
      setLoadingProgress({ current: 0, total: 0 });
    }
  };

  const handleBatchPageChange = (_, value) => {
    setBatchPage(value);
    const container = document.getElementById('image-library-results-top');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const collectLoadedFilesForDownload = () => {
    const filesByKey = new Map();
    const alternatePattern = /[_-][A-Za-z0-9]+(?=\.)/;
    const selectedSizeSet = new Set(selectedSizes);

    const addVariantFiles = (sizeKey, images = []) => {
      if (!selectedSizeSet.has(sizeKey)) {
        return;
      }

      images.forEach((image) => {
        const key = image?.Key;
        if (!key) {
          return;
        }

        const fileName = key.split('/').pop();
        if (!fileName) {
          return;
        }

        if (!includeAlternatesOption && alternatePattern.test(fileName)) {
          return;
        }

        filesByKey.set(key, {
          key,
          name: `${sizeKey}/${fileName}`,
        });
      });
    };

    if (Array.isArray(batchResults) && batchResults.length) {
      batchResults.forEach((entry) => {
        const images = entry?.images || {};
        addVariantFiles('hires', images.hires || []);
        addVariantFiles('large', images.large || []);
        addVariantFiles('small', images.small || []);
        addVariantFiles('thumbnail', images.thumbnail || []);
      });
    } else if (Array.isArray(imageInfo) && imageInfo.length) {
      addVariantFiles('hires', imageInfo[0] || []);
      addVariantFiles('large', imageInfo[1] || []);
      addVariantFiles('small', imageInfo[2] || []);
      addVariantFiles('thumbnail', imageInfo[3] || []);
    }

    return Array.from(filesByKey.values());
  };

  const buildDownloadPayload = () => {
    const files = collectLoadedFilesForDownload();

    if (files.length) {
      return {
        files,
        sizes: selectedSizes,
        includeAlternates: includeAlternatesOption,
      };
    }

    return {
      picRefs: downloadPicRefs,
      sizes: selectedSizes,
      includeAlternates: includeAlternatesOption,
    };
  };

  const summarizeDownloadPayload = (payload) => ({
    fileCount: Array.isArray(payload.files) ? payload.files.length : 0,
    itemCount: Array.isArray(payload.picRefs) ? payload.picRefs.length : downloadPicRefs.length,
  });

  const handleOpenDownloadModal = () => {
    setSelectedSizes([]);
    setIncludeAlternatesOption(false);
    setSharedDownloadLink('');
    setShareLinkJobId('');
    setShareLinkJob(null);
    setDownloadProgressPercent(0);
    setDownloadModalOpen(true);
  };

  const handleCloseDownloadModal = () => {
    if (isDownloading || isGeneratingShareLink) {
      return;
    }
    setDownloadModalOpen(false);
    setSelectedSizes([]);
    setIncludeAlternatesOption(false);
    setSharedDownloadLink('');
    setShareLinkJobId('');
    setShareLinkJob(null);
    setDownloadProgressPercent(0);
  };

  const handleSizeToggle = (_, newSizes) => {
    setSelectedSizes(newSizes || []);
  };

  const handleDownloadConfirm = async () => {
    if (selectedSizes.length === 0 || downloadPicRefs.length === 0) {
      return;
    }

    const payload = buildDownloadPayload();
    let downloadSucceeded = false;

    setActiveDownloadSummary(summarizeDownloadPayload(payload));
    setDownloadStatusStartedAt(Date.now());
    setIsDownloading(true);
    setDownloadProgressPercent(5);

    try {
      const zipBlob = await axios.post('/imagelibrary/download', payload, {
        responseType: 'blob',
        timeout: 0,
        onDownloadProgress: (event) => {
          setDownloadProgressPercent((prev) => {
            if (event.total) {
              const percent = Math.round((event.loaded / event.total) * 100);
              return Math.max(percent, 5);
            }
            if (prev >= 95) {
              return prev;
            }
            return Math.min(prev + 5, 95);
          });
        },
      });

      setDownloadProgressPercent(100);
      downloadSucceeded = true;

      const blob = new Blob([zipBlob.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `image-library-${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      await new Promise((resolve) => window.setTimeout(resolve, 350));

      setDownloadModalOpen(false);
      setSelectedSizes([]);
      setIncludeAlternatesOption(false);
      setDownloadProgressPercent(0);
    } catch (error) {
      console.error('Error downloading images:', error);
      alert('Unable to download images with the selected options.');
    } finally {
      setIsDownloading(false);
      setDownloadStatusStartedAt(null);
      if (!downloadSucceeded) {
        setDownloadProgressPercent(0);
      }
    }
  };

  const handleCreateShareLink = async () => {
    if (selectedSizes.length === 0 || downloadPicRefs.length === 0) {
      return;
    }

    const payload = buildDownloadPayload();
    setActiveDownloadSummary(summarizeDownloadPayload(payload));
    setDownloadStatusStartedAt(Date.now());
    setIsGeneratingShareLink(true);
    setSharedDownloadLink('');
    setShareLinkJob(null);
    setShareLinkJobId('');

    try {
      const response = await axios.post('/imagelibrary/download/share-link/jobs', payload, {
        timeout: 0,
      });

      const nextJob = response?.data || null;
      const nextJobId = nextJob?.jobId;
      if (!nextJobId) {
        throw new Error('Missing share link job ID in response.');
      }

      setShareLinkJob(nextJob);
      setShareLinkJobId(nextJobId);
    } catch (error) {
      console.error('Error generating share link:', error);
      setIsGeneratingShareLink(false);
      setDownloadStatusStartedAt(null);
      alert('Unable to generate a shareable download link with the selected options.');
    }
  };

  const copyShareLink = async () => {
    if (!sharedDownloadLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(sharedDownloadLink);
    } catch (error) {
      alert('Unable to copy the share link.');
    }
  };

  const handleToggleImageSelect = (key, checked) => {
    setSelectedImageKeys((prev) => {
      const set = new Set(prev);
      if (checked) {
        set.add(key);
      } else {
        set.delete(key);
      }
      return Array.from(set);
    });
  };

  const filterImagesForEntry = (entry) => {
    const { images } = entry || {};
    const sizesFilter = filters.sizes;
    const formatsFilter = filters.formats.map((format) => format.toLowerCase());
    const anglesFilter = filters.angles.map((angle) => angle.toUpperCase());
    const filterBySize = sizesFilter.length > 0;
    const filterByFormat = formatsFilter.length > 0;
    const filterByAngle = anglesFilter.length > 0;

    const applyVariantFilter = (list = []) =>
      list.filter((img) => {
        const key = img.Key || '';
        const ext = key.split('.').pop()?.toLowerCase() || '';
        const suffixMatch = key.match(/[_-]([A-Z0-9])(?=\.[^.]+$)/i);
        const angle = suffixMatch ? suffixMatch[1].toUpperCase() : null;
        if (filterByFormat && !formatsFilter.includes(ext)) return false;
        if (filterByAngle && (!angle || !anglesFilter.includes(angle))) return false;
        return true;
      });

    const nextImages = {};
    ['hires', 'large', 'small', 'thumbnail'].forEach((size) => {
      const originalList = Array.isArray(images?.[size]) ? images[size] : [];
      const filteredList = applyVariantFilter(originalList);
      nextImages[size] = filterBySize && !sizesFilter.includes(size) ? [] : filteredList;
    });

    if (filters.missingHires && nextImages.hires?.length) return null;
    if (filters.missingThumbnail && nextImages.thumbnail?.length) return null;

    return { ...entry, images: nextImages };
  };

  const handleConfirmDelete = async () => {
    if (!selectedImageKeys.length) {
      setConfirmDeleteOpen(false);
      return;
    }

    try {
      await axios.post('/imagelibrary/delete', { keys: selectedImageKeys });

      if (Array.isArray(imageInfo) && imageInfo.length === 4) {
        setImageInfo(
          imageInfo.map((arr) =>
            Array.isArray(arr) ? arr.filter((img) => !selectedImageKeys.includes(img.Key)) : []
          )
        );
      }

      if (Array.isArray(batchResults) && batchResults.length > 0) {
        setBatchResults(
          batchResults.map((entry) => {
            const images = entry.images || {};
            const nextImages = {};
            ['hires', 'large', 'small', 'thumbnail'].forEach((size) => {
              nextImages[size] = Array.isArray(images[size])
                ? images[size].filter((img) => !selectedImageKeys.includes(img.Key))
                : [];
            });
            return { ...entry, images: nextImages };
          })
        );
      }

      setSelectedImageKeys([]);
      setConfirmDeleteOpen(false);
    } catch (error) {
      console.error('Error deleting images:', error);
      alert('Unable to delete the selected images.');
    }
  };

  const canDownload = downloadPicRefs.length > 0;
  const selectedCount = selectedImageKeys.length;
  const batchPageSize = 25;
  const batchTotalPages = batchResults
    ? Math.ceil(
        batchResults.filter((entry) => entry.itemNumber || entry.picRef).length / batchPageSize
      )
    : 0;

  return (
    <>
      <Grid
        id='image-library-results-top'
        container
        spacing={2}
        justifyContent='center'
        alignItems='flex-start'
        direction='row'
        sx={{ py: 0, px: { xs: 1, md: 0 } }}
      >
        <Grid item xs={12} sx={{ width: '100%', mb: 2 }}>
          <Paper
            elevation={6}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              px: { xs: 1.5, md: 2.5 },
              py: { xs: 1.15, md: 1.8 },
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
              background: 'linear-gradient(135deg, #1f2937 0%, #111827 60%, #0b0f19 100%)',
              color: 'common.white',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: 0.15,
                pointerEvents: 'none',
                backgroundImage:
                  'radial-gradient(circle at 15% 20%, rgba(255,255,255,0.45), transparent 55%), radial-gradient(circle at 85% 15%, rgba(255,255,255,0.2), transparent 60%)',
              }}
            />
            <Stack
              spacing={{ xs: 1.5, md: 2 }}
              sx={{
                position: 'relative',
                zIndex: 1,
                maxWidth: 960,
                mx: 'auto',
                width: '100%',
                alignItems: 'center',
              }}
            >
              <Grid container spacing={1.5} justifyContent='center' sx={{ maxWidth: 960 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant='filled'
                    size='small'
                    value={itemSearch}
                    onChange={handleItemInput}
                    onKeyDown={handleItemEnterSubmit}
                    label='Search by Item #'
                    placeholder='e.g. 123456'
                    inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
                    InputProps={{
                      disableUnderline: true,
                      endAdornment: (
                        <Tooltip title='Search Item #'>
                          <span>
                            <IconButton
                              size='small'
                              onClick={handleItemSearch}
                              sx={{ color: 'rgba(255,255,255,0.85)' }}
                            >
                              <SearchIcon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ),
                    }}
                    sx={{
                      '& .MuiFilledInput-root': {
                        borderRadius: 2.5,
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        backdropFilter: 'blur(6px)',
                        color: 'common.white',
                        transition: 'background-color 0.2s ease',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                        '&.Mui-focused': { backgroundColor: 'rgba(255,255,255,0.08)' },
                        '& input': {
                          WebkitBoxShadow: '0 0 0 1000px rgba(255,255,255,0.08) inset',
                          WebkitTextFillColor: '#fff',
                          transition: 'background-color 0s',
                        },
                        '& input:-webkit-autofill': {
                          WebkitBoxShadow: '0 0 0 1000px rgba(255,255,255,0.08) inset',
                          WebkitTextFillColor: '#fff',
                        },
                        '& input:-webkit-autofill:focus': {
                          WebkitBoxShadow: '0 0 0 1000px rgba(255,255,255,0.08) inset',
                          WebkitTextFillColor: '#fff',
                        },
                        '& input:-webkit-autofill:hover': {
                          WebkitBoxShadow: '0 0 0 1000px rgba(255,255,255,0.08) inset',
                          WebkitTextFillColor: '#fff',
                        },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                      '& .MuiInputLabel-root.Mui-focused': { color: 'common.white' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                  <Tooltip
                    title={
                      canDownload
                        ? 'Download the image set from your latest search'
                        : 'Run a search before downloading images'
                    }
                  >
                    <span>
                      <Button
                        onClick={handleOpenDownloadModal}
                        startIcon={<CloudDownloadIcon />}
                        variant='outlined'
                        color='inherit'
                        disabled={!canDownload}
                        sx={{
                          borderRadius: 2.5,
                          borderColor: 'rgba(255,255,255,0.4)',
                          color: 'common.white',
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': {
                            borderColor: 'common.white',
                            backgroundColor: 'rgba(255,255,255,0.12)',
                          },
                          '&.Mui-disabled': {
                            borderColor: 'rgba(255,255,255,0.15)',
                            color: 'rgba(255,255,255,0.4)',
                          },
                        }}
                      >
                        Download Images
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    component='label'
                    startIcon={<UploadFileIcon />}
                    variant='outlined'
                    color='inherit'
                    sx={{
                      borderRadius: 2.5,
                      borderColor: 'rgba(255,255,255,0.4)',
                      color: 'common.white',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: 'common.white',
                        backgroundColor: 'rgba(255,255,255,0.12)',
                      },
                    }}
                  >
                    Upload Item CSV
                    <input type='file' accept='.csv' hidden onChange={handleItemFileUpload} />
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </Paper>
        </Grid>

        {batchResults && !isLoadingResults && batchTotalPages > 1 ? (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={batchTotalPages}
              page={batchPage}
              onChange={handleBatchPageChange}
              color='primary'
              shape='rounded'
              siblingCount={1}
              boundaryCount={1}
            />
          </Grid>
        ) : null}

        <Grid item xs={12} sx={{ textAlign: batchResults ? 'left' : 'center', mt: 2 }}>
          {isLoadingResults ? (
            <Box sx={{ mt: 4, maxWidth: 420, mx: 'auto' }}>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Retrieving images...
              </Typography>
              <LinearProgress
                variant={loadingProgress.total > 0 ? 'determinate' : 'indeterminate'}
                value={
                  loadingProgress.total > 0
                    ? Math.min(100, (loadingProgress.current / loadingProgress.total) * 100)
                    : 0
                }
                sx={{ height: 10, borderRadius: 5 }}
              />
              {loadingProgress.total > 0 && (
                <Typography variant='caption' color='text.secondary'>
                  {Math.round((loadingProgress.current / loadingProgress.total) * 100)}% complete
                </Typography>
              )}
            </Box>
          ) : batchResults ? (
            <BatchResults
              results={batchResults}
              page={batchPage}
              pageSize={batchPageSize}
              onPageChange={handleBatchPageChange}
              selectedKeys={selectedImageKeys}
              onToggleSelect={handleToggleImageSelect}
              canDelete={canDelete}
              filterEntry={filterImagesForEntry}
            />
          ) : Array.isArray(imageInfo) &&
            imageInfo.some((column) => Array.isArray(column) && column.length > 0) ? (
            <Box sx={{ width: '100%', mb: 3 }}>
              <Box
                sx={(theme) => ({
                  mb: 2,
                  px: 2.5,
                  py: 1.2,
                  borderRadius: 2,
                  background:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.04)'
                      : 'rgba(0,0,0,0.04)',
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  width: '100%',
                  justifyContent: 'flex-start',
                })}
              >
                <Typography
                  variant='subtitle2'
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    mb: 0,
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Item #
                </Typography>
                <Typography
                  variant='h6'
                  sx={{
                    mb: 0,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: 'text.primary',
                    lineHeight: 1.2,
                  }}
                >
                  {displayItemHeader || 'Item'}
                </Typography>
              </Box>
              <ResolutionContainer
                data={imageInfo}
                selectedKeys={selectedImageKeys}
                onToggleSelect={handleToggleImageSelect}
                canDelete={canDelete}
              />
            </Box>
          ) : searchError ? (
            <Typography variant='body2' color='error' sx={{ mt: 2 }}>
              {searchError}
            </Typography>
          ) : null}
        </Grid>
      </Grid>

      <Dialog open={downloadModalOpen} onClose={handleCloseDownloadModal} fullWidth maxWidth='sm'>
        <DialogTitle>Select Download Options</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {(isDownloading || isGeneratingShareLink) && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                background: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(17,24,39,0.03)',
              }}
            >
              <Typography variant='body2' sx={{ mb: 0.75, fontWeight: 600 }}>
                {isGeneratingShareLink ? 'Generating shareable link' : 'Preparing download'}
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1.25 }}>
                {isGeneratingShareLink
                  ? shareLinkJob?.stage === 'queued'
                    ? 'Your export is queued. It will begin automatically when the server is ready.'
                    : shareLinkJob?.stage === 'uploading'
                    ? 'The zip has been built and is now uploading so one share link can be returned.'
                    : 'Building the zip and preparing the final share link.'
                  : 'Building the zip. Your browser download will begin automatically when it is ready.'}
              </Typography>
              <LinearProgress
                variant={
                  !isGeneratingShareLink && downloadProgressPercent > 5 ? 'determinate' : 'indeterminate'
                }
                value={Math.min(100, downloadProgressPercent)}
                sx={{ height: 8, borderRadius: 4, mb: 1.25 }}
              />
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                Elapsed: {formatDuration(downloadStatusElapsedSeconds)}
              </Typography>
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                {activeDownloadSummary.fileCount > 0
                  ? `Files queued: ${activeDownloadSummary.fileCount.toLocaleString()}`
                  : `Items queued: ${activeDownloadSummary.itemCount.toLocaleString()}`}
              </Typography>
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                {isGeneratingShareLink
                  ? shareLinkJob?.stage === 'queued'
                    ? `Queue position: ${(shareLinkJob?.queuePosition || 1).toLocaleString()}`
                    : shareLinkJob?.stage === 'uploading'
                    ? `Uploaded: ${formatBytes(shareLinkJob?.uploadedBytes || 0)}${
                        shareLinkJob?.totalUploadBytes
                          ? ` of ${formatBytes(shareLinkJob.totalUploadBytes)}`
                          : ''
                      }`
                    : `Archived: ${(shareLinkJob?.processedFiles || 0).toLocaleString()} of ${(
                        shareLinkJob?.fileCount || activeDownloadSummary.fileCount || 0
                      ).toLocaleString()} files`
                  : downloadProgressPercent > 5
                  ? `${Math.round(Math.min(100, downloadProgressPercent))}% transferred to the browser`
                  : 'The server is still assembling the archive.'}
              </Typography>
            </Box>
          )}
          <Stack spacing={3}>
            <Box>
              <Typography
                variant='subtitle2'
                color='text.secondary'
                sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.6, mb: 0.5 }}
              >
                Image Sizes
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                Choose every size you want bundled into the download.
              </Typography>
              <ToggleButtonGroup
                value={selectedSizes}
                onChange={handleSizeToggle}
                aria-label='image sizes'
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 1,
                  width: '100%',
                  '& .MuiToggleButton-root': {
                    borderRadius: 999,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(148,163,184,0.18), rgba(100,116,139,0.14))'
                        : 'linear-gradient(135deg, rgba(17,24,39,0.08), rgba(31,41,55,0.06))',
                    color: 'text.secondary',
                  },
                }}
              >
                {sizeOptions.map((option) => (
                  <ToggleButton
                    key={option.value}
                    value={option.value}
                    aria-label={option.label}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 999,
                      justifyContent: 'center',
                      px: 2,
                      py: 1,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }}
                  >
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {selectedSizes.length === 0 && (
                <FormHelperText error sx={{ mt: 1 }}>
                  Select at least one size to enable download.
                </FormHelperText>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography
                variant='subtitle2'
                color='text.secondary'
                sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.6, mb: 0.5 }}
              >
                Alternate Images
              </Typography>
              <Stack spacing={0.75} alignItems='flex-start'>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeAlternatesOption}
                      onChange={(event) => setIncludeAlternatesOption(event.target.checked)}
                    />
                  }
                  label='Include Alternate Angles (if available)'
                />
                <Typography variant='caption' color='text.secondary' sx={{ pl: 4 }}>
                  Select this to also include images with alternate angle suffixes (e.g. `_A`, `_B`) in
                  the download.
                </Typography>
              </Stack>
            </Box>

            {sharedDownloadLink && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 1.5,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(17,24,39,0.03)',
                }}
              >
                <Typography variant='subtitle2' sx={{ mb: 0.75 }}>
                  Shareable Download Link
                </Typography>
                <TextField value={sharedDownloadLink} size='small' fullWidth InputProps={{ readOnly: true }} />
                <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 1 }}>
                  <Button size='small' variant='outlined' onClick={copyShareLink}>
                    Copy Link
                  </Button>
                  <Typography variant='caption' color='text.secondary'>
                    This link expires in 30 days
                  </Typography>
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDownloadModal} disabled={isDownloading || isGeneratingShareLink}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateShareLink}
            variant='outlined'
            disabled={selectedSizes.length === 0 || isDownloading || isGeneratingShareLink || !canDownload}
          >
            {isGeneratingShareLink ? 'Generating...' : 'Generate Share Link'}
          </Button>
          <Button
            onClick={handleDownloadConfirm}
            variant='contained'
            disabled={selectedSizes.length === 0 || isDownloading || isGeneratingShareLink || !canDownload}
          >
            {isDownloading ? 'Preparing...' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 2 }}>
            Are you sure you want to delete {selectedImageKeys.length} image
            {selectedImageKeys.length === 1 ? '' : 's'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button color='error' variant='contained' onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {selectedCount > 0 && canDelete && (
        <Box
          sx={(theme) => ({
            position: 'fixed',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 4,
            borderRadius: 999,
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            zIndex: 10,
          })}
        >
          <Typography variant='body2'>
            {selectedCount} image{selectedCount === 1 ? '' : 's'} selected
          </Typography>
          <Divider orientation='vertical' flexItem />
          <Button size='small' color='error' variant='contained' onClick={() => setConfirmDeleteOpen(true)}>
            Delete
          </Button>
        </Box>
      )}
    </>
  );
};

export default ImageLibrary;
